import { Typography, styled } from '@mui/material'
import { ExclamationMarkIcon } from '@weblite/icons'
import { useHistoryCustomBack } from 'weblite-router'
import { ActiveSession } from '@weblite/ts-action/auth'
import { SecondaryButton } from '$common/buttons'
import { BottomDrawer } from '$common/bottom-drawer'
import { CancelOutlinedButton } from '$common/buttons/cancel-outlined-button'
import { SessionsList } from './components/sessions-list'
import { useActiveSessions } from '$logic/auth/react-hook'
import { useEffect, useMemo, useState } from 'react'
import { useUserSessionsLimit } from '$logic/hall/react-hook'
import { useOpenedRoomId } from '$logic/opened-room'
import { toLocale } from '$helper/functions/text'
import { t } from '$i18n'

const Root = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  background-color: ${({ theme }) => theme.palette.neutral200};
`

const Section = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  background-color: ${({ theme }) => theme.palette.neutral000};
`

const Actions = styled(Section)`
  padding: 20px;
  flex-shrink: 0;
  flex-direction: row;
  justify-content: space-between;

  & > button {
    width: calc(50% - 6px);
    height: 48px;
  }

  &.one-child {
    & > button {
      width: 100%;
    }
  }
`

const IconContainer = styled('div')`
  width: 100px;
  height: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 32px 0;

  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.primary50};

  & > svg {
    width: 64px;
    height: 64px;
    color: ${({ theme }) => theme.palette.primary500};
  }
`

export interface Props {
  isOpen?: boolean
  onRegister: () => unknown
  onCancel: () => unknown
}

export function UserActiveSessionsLimitModal({
  isOpen = false,
  onRegister,
  onCancel,
}: Props) {
  const roomId = useOpenedRoomId()
  const [isLoadedOnce, setIsLoadedOnce] = useState(false)

  const { value: sessionsLimit, loading: loadingSessionsLimit } =
    useUserSessionsLimit(roomId)

  // NOTE: Prevents closing the modal when the back button is pressed
  useHistoryCustomBack(() => {}, [])

  const {
    value: activeSessions,
    loading: loadingSessions,
    terminateSession,
  } = useActiveSessions()

  const [loadedActiveSessions, setLoadedActiveSessions] = useState<
    ActiveSession[]
  >([])

  useEffect(() => {
    if (activeSessions?.others)
      setLoadedActiveSessions(activeSessions.others ?? [])
  }, [activeSessions])

  const dataIsReady = !loadingSessions && !loadingSessionsLimit

  const canProceed =
    dataIsReady &&
    (sessionsLimit === 0 || loadedActiveSessions.length < sessionsLimit!)

  useEffect(() => {
    if (dataIsReady) setIsLoadedOnce(true)
  }, [dataIsReady])

  const descriptionTranslationKey = canProceed
    ? 'registerDescription'
    : 'notAllowedDescription'

  return (
    <BottomDrawer
      isOpen={(isLoadedOnce || dataIsReady) && isOpen}
      onClose={() => {}}
    >
      <Root>
        <Section sx={{ flexShrink: 0 }}>
          <IconContainer>
            <ExclamationMarkIcon />
          </IconContainer>

          <Section sx={{ px: 2.5, mb: 2 }}>
            <Typography
              variant="h2"
              color="neutral1000"
              sx={{
                textAlign: 'center',
                mb: 2,
              }}
            >
              {t('userActiveSessionsLimitModal.title')}
            </Typography>

            <Typography
              dir="auto"
              color="neutral900"
              variant="paragraph1"
              sx={{
                overflowWrap: 'break-word',
                wordWrap: 'break-word',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                textAlign: 'center',
              }}
            >
              {toLocale(
                t(`userActiveSessionsLimitModal.${descriptionTranslationKey}`)(
                  loadingSessionsLimit ? '...' : sessionsLimit,
                ),
              )}
            </Typography>
          </Section>
        </Section>

        <Section>
          <SessionsList
            activeSessions={loadedActiveSessions}
            terminateSession={terminateSession}
            isLoading={!!loadingSessions && !isLoadedOnce}
            sx={{ maxHeight: '400px' }}
          />
        </Section>

        <Actions className={!canProceed ? 'one-child' : ''}>
          {canProceed && (
            <SecondaryButton
              text={t('userActiveSessionsLimitModal.register')}
              onClick={onRegister}
            />
          )}

          <CancelOutlinedButton
            text={t(canProceed ? 'common.cancel' : 'common.back')}
            onClick={onCancel}
          />
        </Actions>
      </Root>
    </BottomDrawer>
  )
}
